import { messageWindowMs } from './replayPlayback'
import type {
  ReplayBundle,
  ReplayEventFilterKey,
  ReplayGamePin,
  ReplayMarker,
  ReplayMessage,
  ReplayParticipant,
} from './replayTypes'

/**
 * Game pins that have appeared by `cursor`, with hidden kinds removed.
 *
 * A pin appears when playback reaches its timestamp and then stays: a piece of
 * game does not un-happen, and a viewer scrubbing back and forth needs the tally
 * on screen to match the tally in the report.
 */
export function visibleGamePins(
  bundle: ReplayBundle,
  cursor: number,
  hiddenEvents: ReadonlySet<ReplayEventFilterKey>,
): ReplayGamePin[] {
  return bundle.gamePins.filter((pin) => pin.at <= cursor && !hiddenEvents.has(pin.kind))
}

/**
 * Whether a participant's barking indicator should be lit at `cursor`.
 *
 * True when any fix in the trailing `windowMs` of hunt time reported barking —
 * not just the fix the cursor happens to sit on. At high playback speeds the
 * cursor can cross several minutes of hunt time between frames, so testing the
 * single current fix would skip most barking entirely; the window is what makes
 * the indicator survive a fast scan. See `transientWindowMs` for how the window
 * itself is sized.
 */
export function isBarkingAt(
  participant: ReplayParticipant,
  cursor: number,
  windowMs: number,
): boolean {
  // Only a dog bays. A hunter's phone reports no bark rate at all, so this is not
  // load-bearing today — it says so explicitly rather than leaving the behaviour to
  // depend on the absence of a field.
  if (participant.role !== 'dog') return false
  if (cursor < participant.windowStart || cursor > participant.windowEnd) return false

  const from = cursor - windowMs
  const fixes = participant.fixes
  for (let i = lowerBound(fixes, from); i < fixes.length; i++) {
    const fix = fixes[i]
    if (fix.t > cursor) break
    if (fix.barking === true) return true
  }
  return false
}

/** Index of the first fix at or after `t`. */
function lowerBound(fixes: ReplayParticipant['fixes'], t: number): number {
  let low = 0
  let high = fixes.length
  while (low < high) {
    const mid = (low + high) >>> 1
    if (fixes[mid].t < t) low = mid + 1
    else high = mid
  }
  return low
}

/** A message and the marker it is drawn above. */
export interface ReplayMessageBubble {
  marker: ReplayMarker
  message: ReplayMessage
}

/**
 * The chat bubbles to draw at `cursor` — at most one per sender.
 *
 * Only the sender's LATEST in-window message is returned. Every in-window
 * message would pile bubbles on top of each other exactly when the hunt is at
 * its busiest, and the windows here are long: a burst of messages reads as each
 * replacing the last, which is how a chat reads anyway.
 *
 * A sender with no marker contributes nothing, and that falls out of taking the
 * markers as an argument rather than looking them up. It covers two cases at
 * once, both wanted: someone who was in the chat but never carried a tracker,
 * and someone who did carry one but whose window does not reach the moment they
 * wrote. Neither has anywhere on the map to put a bubble. Passing the markers in
 * also means this shares the one interpolation pass the marker layer already
 * runs each frame, instead of a second one.
 */
export function visibleMessagesAt(
  bundle: ReplayBundle,
  cursor: number,
  speed: number,
  markers: readonly ReplayMarker[],
): ReplayMessageBubble[] {
  const messages = bundle.messages
  if (messages === undefined || messages.length === 0) return []

  const latest = new Map<string, ReplayMessage>()
  const onMap = new Set(markers.map((marker) => marker.participant.id))

  for (const message of messages) {
    if (!onMap.has(message.authorId)) continue
    if (message.at > cursor) continue
    if (cursor >= message.at + messageWindowMs(message.text, speed)) continue

    const held = latest.get(message.authorId)
    if (held === undefined || message.at > held.at) latest.set(message.authorId, message)
  }

  // Emitted in marker order rather than message order, so bubbles keep the same
  // paint order as the markers they belong to from one frame to the next.
  const bubbles: ReplayMessageBubble[] = []
  for (const marker of markers) {
    const message = latest.get(marker.participant.id)
    if (message !== undefined) bubbles.push({ marker, message })
  }
  return bubbles
}

/**
 * How many of each event type the hunt holds, for the filter rows' counts.
 *
 * Barking is counted as the number of dogs that barked at all, not the number of
 * barking fixes: a single bay spans dozens of consecutive fixes, so a fix count
 * would read as thousands of events and tell the viewer nothing.
 *
 * Messages are counted as the ones that CAN be shown — those whose sender is on
 * the hunt's roster — rather than every message in the payload. A count that
 * included messages the viewer can never draw would not match the map, and the
 * count is there to describe the map.
 */
export function replayEventCounts(bundle: ReplayBundle): Record<ReplayEventFilterKey, number> {
  const counts: Record<ReplayEventFilterKey, number> = {
    observed: 0,
    downed: 0,
    wounded: 0,
    barking: 0,
    messages: 0,
  }
  for (const pin of bundle.gamePins) counts[pin.kind]++
  for (const participant of bundle.participants) {
    if (participant.role !== 'dog') continue
    if (participant.fixes.some((fix) => fix.barking === true)) counts.barking++
  }
  if (bundle.messages !== undefined) {
    const carriers = new Set(bundle.participants.map((participant) => participant.id))
    for (const message of bundle.messages) {
      if (carriers.has(message.authorId)) counts.messages++
    }
  }
  return counts
}

/**
 * Bounding box of everything the replay can draw, so the map can open framed on
 * the hunt rather than on a default view the hunter then has to find their way
 * from. Null when there is nothing with a position at all.
 */
export function replayBounds(
  bundle: ReplayBundle,
): { minLat: number; maxLat: number; minLon: number; maxLon: number } | null {
  let minLat = Number.POSITIVE_INFINITY
  let maxLat = Number.NEGATIVE_INFINITY
  let minLon = Number.POSITIVE_INFINITY
  let maxLon = Number.NEGATIVE_INFINITY
  let seen = false

  const include = (lat: number, lon: number) => {
    seen = true
    if (lat < minLat) minLat = lat
    if (lat > maxLat) maxLat = lat
    if (lon < minLon) minLon = lon
    if (lon > maxLon) maxLon = lon
  }

  for (const participant of bundle.participants) {
    for (const fix of participant.fixes) include(fix.lat, fix.lon)
  }
  for (const pin of bundle.gamePins) include(pin.lat, pin.lon)

  return seen ? { minLat, maxLat, minLon, maxLon } : null
}
